import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Trash2, Plus, ShieldOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AppHeader from '../components/AppHeader';
import Breadcrumb from '../components/Breadcrumb';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import {
  PageContainer, PageHeader, PageTitle, PageSubtitle,
  Button, Label, FormGroup, Input,
  EmptyState, EmptyIcon, EmptyText,
  ModalOverlay, ModalBox, ModalHeader, ModalTitle, ModalBody, ModalFooter,
  LoadingContainer, Spinner, Badge
} from '../components/ui';
import { theme } from '../styles/theme';
import api from '../services/api';

const SOURCE_LABEL = { webhook: 'WhatsApp', manual: 'Manual', user: 'Usuário' };
const SOURCE_VARIANT = { webhook: 'warning', manual: 'neutral', user: 'info' };

async function fetchCampaignName(tenantId, campaignId, cache) {
  if (!campaignId) return null;
  if (cache[campaignId]) return cache[campaignId];
  try {
    const res = await api.get(`/campaigns/${tenantId}/${campaignId}`);
    const name = res.data?.name || campaignId;
    cache[campaignId] = name;
    return name;
  } catch { return campaignId; }
}

export default function OptOutBlacklistPage() {
  const { t } = useTranslation('optout');
  const { tenantId } = useParams();
  const toast   = useToast();
  const confirm = useConfirm();

  const [list, setList]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [phone, setPhone]     = useState('');
  const [saving, setSaving]   = useState(false);
  const [search, setSearch]   = useState('');
  const [campaignNames, setCampaignNames] = useState({});

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const breadcrumbItems = [
    user.role === 'sallus_admin'
      ? { label: 'Libben Agents', path: '/sallus/dashboard' }
      : { label: t('home', { ns: 'common' }), path: `/dashboard/${tenantId}` },
    { label: t('campaigns', { ns: 'common' }), path: `/campaigns/${tenantId}` },
    { label: t('title') }
  ];

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/optout/${tenantId}`);
      const items = res.data.optouts || [];
      setList(items);
      // Busca nomes das campanhas referenciadas
      const cache = {};
      const ids = [...new Set(items.map(i => i.campaignId).filter(Boolean))];
      await Promise.all(ids.map(id => fetchCampaignName(tenantId, id, cache)));
      setCampaignNames({ ...cache });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!phone.trim()) { toast.error(t('phoneRequired')); return; }
    setSaving(true);
    try {
      await api.post(`/optout/${tenantId}`, { phone: phone.trim() });
      toast.success(t('added'));
      setShowAdd(false);
      setPhone('');
      load();
    } catch (e) { toast.error(e.response?.data?.message || e.message); }
    finally { setSaving(false); }
  };

  const handleRemove = async (item) => {
    const ok = await confirm({ title: t('removeConfirm', { phone: item.phone }), danger: true });
    if (!ok) return;
    try {
      await api.delete(`/optout/${tenantId}/${item.phone}`);
      toast.success(t('removed'));
      load();
    } catch (e) { toast.error(e.response?.data?.message || e.message); }
  };

  const filtered = search
    ? list.filter(i => i.phone.includes(search.replace(/\D/g, '')))
    : list;

  if (loading) return (
    <>
      <AppHeader title={t('title')} />
      <PageContainer><LoadingContainer><Spinner /><span>{t('loading', { ns: 'common' })}</span></LoadingContainer></PageContainer>
    </>
  );

  return (
    <>
      <AppHeader title={t('title')} />
      <PageContainer>
        <Breadcrumb items={breadcrumbItems} />
        <PageHeader>
          <div>
            <PageTitle>🚫 {t('title')}</PageTitle>
            <PageSubtitle>{t('subtitle', { count: list.length })}</PageSubtitle>
          </div>
          <Button variant="primary" size="sm" onClick={() => setShowAdd(true)}>
            <Plus size={14} /> {t('add')}
          </Button>
        </PageHeader>

        {list.length > 0 && (
          <SearchRow>
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('searchPlaceholder')}
              style={{ maxWidth: 280 }}
            />
          </SearchRow>
        )}

        {filtered.length === 0 ? (
          <EmptyState>
            <EmptyIcon><ShieldOff size={48} strokeWidth={1} /></EmptyIcon>
            <EmptyText>{list.length === 0 ? t('empty') : t('noResults')}</EmptyText>
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('phone')}</Th>
                <Th>{t('source')}</Th>
                <Th>{t('date')}</Th>
                <Th>{t('connector')}</Th>
                <Th>{t('campaign')}</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {filtered.map(item => (
                <tr key={item.phone}>
                  <Td><PhoneNum>{formatPhone(item.phone)}</PhoneNum></Td>
                  <Td>
                    <Badge variant={SOURCE_VARIANT[item.source] || 'neutral'}>
                      {SOURCE_LABEL[item.source] || item.source}
                    </Badge>
                  </Td>
                  <Td>{item.createdAt ? new Date(item.createdAt).toLocaleString('pt-BR') : '—'}</Td>
                  <Td style={{ fontSize: theme.typography.xs, color: theme.colors.textMuted }}>{item.connectorId || '—'}</Td>
                  <Td style={{ fontSize: theme.typography.xs, color: theme.colors.textMuted }}>
                    {item.campaignId ? (campaignNames[item.campaignId] || item.campaignId) : '—'}
                  </Td>
                  <Td>
                    <Button variant="danger" size="sm" onClick={() => handleRemove(item)} title={t('remove', { ns: 'common' })}>
                      <Trash2 size={12} />
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {showAdd && (
          <ModalOverlay onClick={() => setShowAdd(false)}>
            <ModalBox maxWidth="400px" onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>{t('addTitle')}</ModalTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowAdd(false)}>✕</Button>
              </ModalHeader>
              <ModalBody>
                <FormGroup>
                  <Label>{t('phoneField')} *</Label>
                  <Input
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    placeholder="5511999999999"
                    autoFocus
                  />
                  <Helper>{t('phoneHint')}</Helper>
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button variant="secondary" onClick={() => setShowAdd(false)}>{t('cancel', { ns: 'common' })}</Button>
                <Button variant="primary" onClick={handleAdd} disabled={saving}>
                  {saving ? t('saving', { ns: 'common' }) : t('add')}
                </Button>
              </ModalFooter>
            </ModalBox>
          </ModalOverlay>
        )}
      </PageContainer>
    </>
  );
}

function formatPhone(phone) {
  if (!phone) return '';
  const d = String(phone).replace(/\D/g, '');
  if (d.length === 13) return `+${d.slice(0,2)} (${d.slice(2,4)}) ${d.slice(4,9)}-${d.slice(9)}`;
  if (d.length === 12) return `+${d.slice(0,2)} (${d.slice(2,4)}) ${d.slice(4,8)}-${d.slice(8)}`;
  return phone;
}

const SearchRow = styled.div`margin-bottom: ${theme.spacing[4]};`;
const Table     = styled.table`width: 100%; border-collapse: collapse; background: white; border-radius: ${theme.radii.lg}; overflow: hidden; box-shadow: ${theme.shadows.sm};`;
const Th        = styled.th`text-align: left; padding: ${theme.spacing[3]} ${theme.spacing[4]}; font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textSecondary}; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid ${theme.colors.border}; background: ${theme.colors.gray50};`;
const Td        = styled.td`padding: ${theme.spacing[3]} ${theme.spacing[4]}; font-size: ${theme.typography.sm}; border-bottom: 1px solid ${theme.colors.border}; color: ${theme.colors.text};`;
const PhoneNum  = styled.span`font-family: monospace; font-weight: ${theme.typography.medium};`;
const Helper    = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; margin-top: ${theme.spacing[1]};`;
